import {
BarChart3,
ChevronLeft,
ChevronRight,
Droplets,
Flame,
HeartPulse,
Home,
Lightbulb,
Loader2,
MessageCircle,
Plus,
Target,
User
} from 'lucide-react';
import React,{ useCallback,useEffect,useMemo,useRef,useState } from 'react';
import {
AchievementsOverview,
FriendChallengeGoalType,
MiniAppAdvice,
MiniAppProfile,
MiniAppStats,
MiniAppWaterSummary,
SocialOverview,
SocialReactionPrompt,
useApi
} from '../services/api';
import { getCoursePresentation } from './coursePresentation';
import { getOnboardingVariant } from './onboardingVariant';
import { AddMealChoiceSheet,AddMealPhotoScreen,AddMealTextScreen,DeleteMealConfirmSheet,MealActionsSheet,MealDetailScreen,MealPortionSheet,SwipeableMealRow } from './screens/MealScreens';
import { ActivityDaySnapshot,AdviceModalOverlay,AnimatedNumber,BotLaunchRootScreen,HomeLoadResult,MAX_MEAL_PORTION_PERCENT,MacroCard,MacroKey,MealNotificationSnapshot,ProfileView,ProgressRing,SkeletonMealRow,SocialReactionPromptOverlay,SocialRouteTarget,StandaloneV2Screen,V2Meal,V2NotificationItem,V2NotificationOverlay,V2TabId,addDays,clampPercent,classifyCalorieMilestone,clearV2SessionCacheByPrefix,formatDisplayName,formatLocalDate,formatLongDate,formatMonthTitle,formatWeekday,getActivityGoalByLevel,getDayOffset,getMacroTheme,getMealConsumedPercent,getWeekDates,isSameDay,mealTypeDefinitions,pluralize,readV2SessionCache,requestManagedMiniAppBack,roundNumber,startOfWeek,useHorizontalSwipeGesture,useMiniAppAuth,writeV2SessionCache } from './shared';
import './styles.css';

// Ленивая загрузка вкладок: главный экран открывается сразу, остальные подгружаются чанками
const loadProfileScreen = () => import('./screens/ProfileScreen');
const loadMealPlannerScreen = () => import('./screens/MealPlannerScreen');
const loadStatisticsScreen = () => import('./screens/StatisticsScreen');
const loadActivityScreen = () => import('./screens/ActivityScreen');
const loadSocialScreens = () => import('./screens/SocialScreens');
const loadStreakScreen = () => import('./screens/StreakScreen');
const loadOnboardingScreen = () => import('./screens/OnboardingScreen');

const ProfileScreen = React.lazy(() => loadProfileScreen().then((m) => ({ default: m.ProfileScreen })));
const MealPlannerScreen = React.lazy(() => loadMealPlannerScreen().then((m) => ({ default: m.MealPlannerScreen })));
const StatisticsScreen = React.lazy(() => loadStatisticsScreen().then((m) => ({ default: m.StatisticsScreen })));
const ActivityScreen = React.lazy(() => loadActivityScreen().then((m) => ({ default: m.ActivityScreen })));
const CommunityScreen = React.lazy(() => loadSocialScreens().then((m) => ({ default: m.CommunityScreen })));
const StreakScreen = React.lazy(() => loadStreakScreen().then((m) => ({ default: m.StreakScreen })));
const OnboardingScreen = React.lazy(() => loadOnboardingScreen().then((m) => ({ default: m.OnboardingScreen })));

// Догружаем чанки экранов в фоне сразу после старта, чтобы переключение вкладок было мгновенным
function preloadScreenChunks() {
  loadStatisticsScreen().catch(() => {});
  loadActivityScreen().catch(() => {});
  loadSocialScreens().catch(() => {});
  loadProfileScreen().catch(() => {});
  loadMealPlannerScreen().catch(() => {});
  loadStreakScreen().catch(() => {});
}

export const WATER_QUICK_AMOUNT_ML = 250;
export const WATER_PICKER_AMOUNTS = [50, 100, 150, 200, 300, 500, 750, 1000];

export function formatWaterAmount(amountMl: number): string {
  if (amountMl >= 1000 && amountMl % 1000 === 0) return `${amountMl / 1000} л`;
  return `${amountMl} мл`;
}

export function WaterTrackerButton({
  totalMl,
  targetMl,
  isSaving,
  disabled,
  splashKey,
  onAdd,
}: {
  totalMl: number;
  targetMl: number;
  isSaving: boolean;
  disabled: boolean;
  splashKey: number;
  onAdd: (amountMl: number) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerCancelling, setPickerCancelling] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(WATER_QUICK_AMOUNT_ML);
  const selectedAmountRef = useRef(WATER_QUICK_AMOUNT_ML);
  const pickerRef = useRef<HTMLSpanElement | null>(null);
  const holdTimerRef = useRef<number | null>(null);
  const longPressRef = useRef(false);
  const pointerIdRef = useRef<number | null>(null);
  const cancelRef = useRef(false);
  const progress = Math.max(0, Math.min(100, targetMl > 0 ? (totalMl / targetMl) * 100 : 0));
  const selectedIndex = Math.max(0, WATER_PICKER_AMOUNTS.indexOf(selectedAmount));
  const selectedRatio = WATER_PICKER_AMOUNTS.length > 1
    ? selectedIndex / (WATER_PICKER_AMOUNTS.length - 1)
    : 0;
  const pickerFillWidth = `calc(${selectedRatio * 100}% - ${selectedRatio * 26}px)`;

  const clearHoldTimer = useCallback(() => {
    if (holdTimerRef.current != null) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }, []);

  const selectAmountFromClientX = useCallback((clientX: number) => {
    const rect = pickerRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const index = Math.max(0, Math.min(WATER_PICKER_AMOUNTS.length - 1, Math.round(ratio * (WATER_PICKER_AMOUNTS.length - 1))));
    const nextAmount = WATER_PICKER_AMOUNTS[index] || WATER_QUICK_AMOUNT_ML;
    selectedAmountRef.current = nextAmount;
    setSelectedAmount(nextAmount);
  }, []);

  const updateCancelState = useCallback((clientX: number, clientY: number) => {
    const rect = pickerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const outsideX = clientX < rect.left - 18 || clientX > rect.right + 18;
    const outsideY = clientY < rect.top - 28 || clientY > rect.bottom + 28;
    const shouldCancel = outsideX || outsideY;
    cancelRef.current = shouldCancel;
    setPickerCancelling(shouldCancel);
  }, []);

  const finishPointer = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (disabled || isSaving) return;
    clearHoldTimer();

    if (pointerIdRef.current === event.pointerId) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // noop
      }
    }
    pointerIdRef.current = null;

    if (longPressRef.current) {
      updateCancelState(event.clientX, event.clientY);
      if (cancelRef.current) {
        setPickerOpen(false);
        setPickerCancelling(false);
        longPressRef.current = false;
        cancelRef.current = false;
        return;
      }
      selectAmountFromClientX(event.clientX);
      setPickerOpen(false);
      setPickerCancelling(false);
      longPressRef.current = false;
      onAdd(selectedAmountRef.current);
      return;
    }

    onAdd(WATER_QUICK_AMOUNT_ML);
  }, [clearHoldTimer, disabled, isSaving, onAdd, selectAmountFromClientX, updateCancelState]);

  const cancelPointer = useCallback((event?: React.PointerEvent<HTMLButtonElement>) => {
    clearHoldTimer();
    longPressRef.current = false;
    cancelRef.current = false;
    pointerIdRef.current = null;
    setPickerOpen(false);
    setPickerCancelling(false);
    if (event) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // noop
      }
    }
  }, [clearHoldTimer]);

  return (
    <div className="relative mt-3.5">
      <button
        type="button"
        className={`noma-v2-water-button ${pickerOpen ? 'is-picking' : ''}`}
        aria-disabled={disabled || isSaving}
        tabIndex={disabled ? -1 : 0}
        onPointerDown={(event) => {
          if (disabled || isSaving) return;
          pointerIdRef.current = event.pointerId;
          longPressRef.current = false;
          cancelRef.current = false;
          setPickerCancelling(false);
          selectedAmountRef.current = WATER_QUICK_AMOUNT_ML;
          setSelectedAmount(WATER_QUICK_AMOUNT_ML);
          try {
            event.currentTarget.setPointerCapture(event.pointerId);
          } catch {
            // noop
          }
          clearHoldTimer();
          holdTimerRef.current = window.setTimeout(() => {
            longPressRef.current = true;
            setPickerOpen(true);
            selectAmountFromClientX(event.clientX);
          }, 320);
        }}
        onPointerMove={(event) => {
          if (!longPressRef.current) return;
          updateCancelState(event.clientX, event.clientY);
          if (cancelRef.current) return;
          selectAmountFromClientX(event.clientX);
        }}
        onPointerUp={finishPointer}
        onPointerCancel={cancelPointer}
        onPointerLeave={(event) => {
          if (!longPressRef.current) cancelPointer(event);
        }}
        onKeyDown={(event) => {
          if ((event.key === 'Enter' || event.key === ' ') && !disabled && !isSaving) {
            event.preventDefault();
            onAdd(WATER_QUICK_AMOUNT_ML);
          }
        }}
      >
        {pickerOpen ? (
          <span
            ref={pickerRef}
            className={`noma-v2-water-picker noma-v2-water-picker--full ${pickerCancelling ? 'is-cancelling' : ''}`}
          >
            <span className="noma-v2-water-picker__value">{pickerCancelling ? 'Отпустить, чтобы отменить' : formatWaterAmount(selectedAmount)}</span>
            <span className="noma-v2-water-picker__track">
              <span className="noma-v2-water-picker__fill" style={{ width: pickerFillWidth }} />
              {WATER_PICKER_AMOUNTS.map((amount) => {
                const active = amount === selectedAmount;
                return (
                  <span key={amount} className={`noma-v2-water-picker__mark ${active ? 'is-active' : ''}`}>
                    <span />
                  </span>
                );
              })}
            </span>
            <span className="noma-v2-water-picker__labels">
              <span>50 мл</span>
              <span>1 л</span>
            </span>
          </span>
        ) : (
          <>
          <span className="noma-v2-water-button__icon">
            <Droplets className="h-6 w-6" />
          </span>
          <span className="min-w-0 flex-1 overflow-hidden">
            <span className="flex min-w-0 items-baseline justify-between gap-2">
              <span className="shrink-0 text-[13px] font-black uppercase tracking-[0.14em] text-[#42bff7]">Вода</span>
              <span className="min-w-0 truncate text-right text-[18px] font-black tracking-[-0.04em] text-[#111113]">
                <AnimatedNumber value={totalMl} /> / {targetMl} мл
              </span>
            </span>
            <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-[rgba(17,17,19,0.08)]">
              <span className="block h-full rounded-full bg-[#57c7ff] transition-[width] duration-300" style={{ width: `${progress}%` }} />
            </span>
          </span>
          </>
        )}
        {!pickerOpen ? (
          isSaving ? <Loader2 className="h-5 w-5 shrink-0 animate-spin text-[#42bff7]" /> : <ChevronRight className="h-6 w-6 shrink-0 text-[#c0c0c9]" />
        ) : null}
      </button>

      {splashKey > 0 ? (
        <div key={splashKey} className="noma-v2-water-splash" aria-hidden="true">
          <span>💧</span>
          <span>💦</span>
          <span>💧</span>
          <span>💦</span>
        </div>
      ) : null}
    </div>
  );
}

export default function AppV2() {
  return (
    <React.Suspense
      fallback={(
        <div className="noma-v2-screen flex min-h-screen items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#7f54ff]" />
        </div>
      )}
    >
      <AppV2Inner />
    </React.Suspense>
  );
}

function AppV2Inner() {
  const api = useApi();
  const onboardingVariant = getOnboardingVariant();
  const ensureMiniAppAuth = useMiniAppAuth();
  const today = useMemo(() => new Date(), []);
  const todayWeekStart = useMemo(() => startOfWeek(today), [today]);
  const weekMarkersCacheRef = useRef<Map<string, Set<string>>>(new Map());
  const weekTransitionResetRef = useRef<number | null>(null);

  const [activeTab, setActiveTab] = useState<V2TabId>('home');
  const [showProfileHint, setShowProfileHint] = useState(false);

  // Переключение вкладок как transition: пока чанк экрана грузится,
  // остаётся текущий экран вместо Suspense-фолбэка (белый экран с лоадером)
  const switchTab = useCallback((tab: V2TabId) => {
    React.startTransition(() => setActiveTab(tab));
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(preloadScreenChunks, 800);
    return () => window.clearTimeout(timer);
  }, []);

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [weekStartDate, setWeekStartDate] = useState<Date>(todayWeekStart);
  const [weekTransitionDirection, setWeekTransitionDirection] = useState<'left' | 'right' | null>(null);
	  const [profile, setProfile] = useState<MiniAppProfile | null>(null);

  useEffect(() => {
    if (!profile) return;
    try {
      if (localStorage.getItem('noma_v2_profile_hint_seen') === '1') return;
      localStorage.setItem('noma_v2_profile_hint_seen', '1');
      setShowProfileHint(true);
      const timer = window.setTimeout(() => setShowProfileHint(false), 5500);
      return () => window.clearTimeout(timer);
    } catch {
      return;
    }
  }, [profile]);
	  const [dayStats, setDayStats] = useState<MiniAppStats | null>(null);
	  const [dayMeals, setDayMeals] = useState<V2Meal[]>([]);
	  const [homeDataDateKey, setHomeDataDateKey] = useState<string | null>(null);
	  // Защита от гонки: при быстром переключении дней более старый (медленный) ответ
	  // loadData может прилететь ПОСЛЕ нового и перезаписать состояние устаревшими данными.
	  // Коммитим в state только результат самого последнего вызова.
	  const loadDataRequestIdRef = useRef(0);
	  const [weekMealDays, setWeekMealDays] = useState<Set<string>>(new Set());
  const [selectedMeal, setSelectedMeal] = useState<V2Meal | null>(null);
  const [standaloneScreen, setStandaloneScreen] = useState<StandaloneV2Screen>(null);
  const [socialRouteTarget, setSocialRouteTarget] = useState<SocialRouteTarget | null>(null);
  const [botLaunchRootScreen, setBotLaunchRootScreen] = useState<BotLaunchRootScreen>(null);
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>(() => {
    try {
      return localStorage.getItem('miniapp_v2_theme_mode') === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });
  const [weekStatsView, setWeekStatsView] = useState<MiniAppStats | null>(null);
  const [weekActivitySeries, setWeekActivitySeries] = useState<ActivityDaySnapshot[]>([]);
  const [activityDay, setActivityDay] = useState<ActivityDaySnapshot | null>(null);
  const [homeActivitySummary, setHomeActivitySummary] = useState<{ totalCalories: number; totalDuration: number; count: number } | null>(null);
  const [activityCaloriesMode, setActivityCaloriesMode] = useState<'add_to_norm' | 'show_separate'>('show_separate');
  const [waterSummary, setWaterSummary] = useState<MiniAppWaterSummary | null>(null);
  const [waterSaving, setWaterSaving] = useState(false);
  const [waterSplashKey, setWaterSplashKey] = useState(0);
  const [statsViewLoading, setStatsViewLoading] = useState(false);
  const [activityViewLoading, setActivityViewLoading] = useState(false);
  const [socialOverview, setSocialOverview] = useState<SocialOverview | null>(null);
  const [socialViewLoading, setSocialViewLoading] = useState(false);
  const [achievementsOverview, setAchievementsOverview] = useState<AchievementsOverview | null>(null);
  const [achievementsLoading, setAchievementsLoading] = useState(false);
  const [achievementsError, setAchievementsError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [onboardingRequired, setOnboardingRequired] = useState<boolean>(() => {
    try {
      return localStorage.getItem('miniapp_onboarding_required') === 'true';
    } catch {
      return false;
    }
  });
  const [tipsPrompt, setTipsPrompt] = useState('Сгенерировать подсказку');
  const [tipsAdvice, setTipsAdvice] = useState<MiniAppAdvice | null>(null);
  const [tipsModalOpen, setTipsModalOpen] = useState(false);
  const [tipsModalLoading, setTipsModalLoading] = useState(false);
  const [deletingMealId, setDeletingMealId] = useState<string | null>(null);
  const [mealPendingDelete, setMealPendingDelete] = useState<V2Meal | null>(null);
  const [mealActionTarget, setMealActionTarget] = useState<V2Meal | null>(null);
  const [addMealChoiceOpen, setAddMealChoiceOpen] = useState(false);
  const [onboardingCoachStep, setOnboardingCoachStep] = useState<number | null>(() => {
    try {
      const pending = localStorage.getItem('noma_v2_onboarding_coach_pending');
      if (pending !== '2' && pending !== '3') return null;
      return Math.max(0, Math.min(4, Number(localStorage.getItem('noma_v2_onboarding_coach_progress') || 0)));
    } catch { return null; }
  });
  const [mealPendingPortion, setMealPendingPortion] = useState<V2Meal | null>(null);
  const [mealConsumedDraft, setMealConsumedDraft] = useState<number>(MAX_MEAL_PORTION_PERCENT);
  const [updatingMealPortionId, setUpdatingMealPortionId] = useState<string | null>(null);
  const [updatingMealWeightId, setUpdatingMealWeightId] = useState<string | null>(null);
  const [shownNotificationKeys, setShownNotificationKeys] = useState<Set<string>>(new Set());
  const [notificationsHydrated, setNotificationsHydrated] = useState(false);
  const [notificationQueue, setNotificationQueue] = useState<V2NotificationItem[]>([]);
  const [activeNotification, setActiveNotification] = useState<V2NotificationItem | null>(null);
  const [notificationLeaving, setNotificationLeaving] = useState(false);
  const [socialPromptQueue, setSocialPromptQueue] = useState<SocialReactionPrompt[]>([]);
  const [activeSocialPrompt, setActiveSocialPrompt] = useState<SocialReactionPrompt | null>(null);
  const [socialPromptActionId, setSocialPromptActionId] = useState<string | null>(null);
  const closeSelectedMeal = useCallback((mealId?: string | null) => {
    const targetMealId = mealId || selectedMeal?.id || null;
    requestManagedMiniAppBack(
      targetMealId ? `meal:${targetMealId}` : null,
      () => setSelectedMeal(null)
    );
  }, [selectedMeal?.id]);
  const consumeSocialChallengeRouteTarget = useCallback(() => {
    setSocialRouteTarget((current) => current ? { ...current, challengeId: null } : null);
  }, []);
  const notificationsUserKeyRef = useRef<string | null>(null);
  const pendingNotificationIdsRef = useRef<Set<string>>(new Set());
  const lastStreakNotificationRef = useRef(0);
  const notificationAutoCloseRef = useRef<number | null>(null);
  const notificationDismissRef = useRef<number | null>(null);
  useEffect(() => {
    try {
      localStorage.setItem('miniapp_v2_theme_mode', themeMode);
    } catch {
      // noop
    }

    document.documentElement.classList.toggle('miniapp-v2-dark', themeMode === 'dark');
  }, [themeMode]);

  useEffect(() => {
    const tg = (window as any).Telegram?.WebApp;
    tg?.ready?.();
    tg?.expand?.();
    try {
      const useDarkShell = themeMode === 'dark';
      tg?.setHeaderColor?.(useDarkShell ? '#111113' : '#bcaaf8');
      tg?.setBackgroundColor?.(useDarkShell ? '#111113' : '#f3f1fb');
      if (tg?.MainButton) {
        tg.MainButton.color = '#7f54ff';
        tg.MainButton.textColor = '#ffffff';
      }
    } catch {
      // noop
    }
  }, [themeMode]);

  useEffect(() => {
    const syncOnboardingRequired = () => {
      try {
        setOnboardingRequired(localStorage.getItem('miniapp_onboarding_required') === 'true');
      } catch {
        setOnboardingRequired(false);
      }
    };

    syncOnboardingRequired();
    window.addEventListener('miniapp_onboarding_required_changed', syncOnboardingRequired);
    window.addEventListener('storage', syncOnboardingRequired);
    return () => {
      window.removeEventListener('miniapp_onboarding_required_changed', syncOnboardingRequired);
      window.removeEventListener('storage', syncOnboardingRequired);
    };
  }, []);

  useEffect(() => {
    const resolveProfileView = (params: URLSearchParams): ProfileView => {
      const section = params.get('section');
      if (section === 'personal' || section === 'notifications' || section === 'subscription' || section === 'app' || section === 'main' || section === 'social' || section === 'achievements') {
        return section;
      }

      if (params.get('settings') === 'true') return 'app';
      return 'main';
    };

    const applyRoute = (value?: string | null, params?: URLSearchParams) => {
      const view = String(value || '').trim();
      if (!view) return;
      const isBotEntry = params?.get('entry') === 'bot';

      if (view === 'statistics') {
        switchTab('statistics');
        if (isBotEntry) setBotLaunchRootScreen(null);
        return;
      }

      if (view === 'activity') {
        switchTab('activity');
        if (isBotEntry) setBotLaunchRootScreen(null);
        return;
      }

      if (view === 'social' || view === 'community') {
        switchTab('social');
        setSocialRouteTarget({
          section: params?.get('section') === 'challenges' ? 'challenges' : null,
          challengeId: params?.get('challengeId') || null,
        });
        if (isBotEntry) setBotLaunchRootScreen(null);
        return;
      }

      if (view === 'meal-planner') {
        setStandaloneScreen({ type: 'meal-planner' });
        setBotLaunchRootScreen(isBotEntry ? { type: 'meal-planner' } : null);
        return;
      }

      if (view === 'subscription') {
        setStandaloneScreen({ type: 'profile', initialView: 'subscription', standalone: true });
        setBotLaunchRootScreen(isBotEntry ? { type: 'profile', view: 'subscription' } : null);
        return;
      }

      if (view === 'achievements') {
        setStandaloneScreen({ type: 'profile', initialView: 'achievements', standalone: true });
        setBotLaunchRootScreen(isBotEntry ? { type: 'profile', view: 'achievements' } : null);
        return;
      }

      if (view === 'profile') {
        const initialView = resolveProfileView(params || new URLSearchParams());
        const focus = params?.get('focus') === 'weight' ? 'weight' : null;
        setStandaloneScreen({ type: 'profile', initialView, standalone: initialView !== 'main', initialFocus: focus });
        setBotLaunchRootScreen(isBotEntry ? { type: 'profile', view: initialView } : null);
        return;
      }

      if (view === 'home') {
        setActiveTab('home');
        if (isBotEntry) setBotLaunchRootScreen(null);
      }
    };

    try {
      if (window.location.pathname.includes('/payment/return')) {
        setStandaloneScreen({ type: 'profile', initialView: 'subscription', standalone: true });
      }

      const params = new URLSearchParams(window.location.search);
      const queryStartapp = params.get('startapp');
      if (queryStartapp) {
        applyRoute(queryStartapp, params);
      } else {
        applyRoute(params.get('view'), params);
      }

      if (window.location.hash) {
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#\/?/, ''));
        const hashStartapp = hashParams.get('startapp');
        if (hashStartapp) {
          applyRoute(hashStartapp, hashParams);
        } else if (!queryStartapp) {
          applyRoute(hashParams.get('view'), hashParams);
        }
      }

      const tgStart = (window as any).Telegram?.WebApp?.initDataUnsafe?.start_param;
      if (tgStart && !queryStartapp) {
        applyRoute(tgStart, params);
      }
    } catch {
      // noop
    }
  }, []);

  useEffect(() => {
    if (!botLaunchRootScreen) return;
    if (!standaloneScreen) {
      setBotLaunchRootScreen(null);
      return;
    }
    if (botLaunchRootScreen.type !== standaloneScreen.type) {
      setBotLaunchRootScreen(null);
    }
  }, [botLaunchRootScreen, standaloneScreen]);

  const notificationUserKey = useMemo(() => {
    const telegramId = (window as any).Telegram?.WebApp?.initDataUnsafe?.user?.id;
    if (telegramId) return `tg:${String(telegramId)}`;
    const profileUserId = String(profile?.user?.id || '').trim();
    return profileUserId ? `user:${profileUserId}` : '';
  }, [profile?.user?.id]);

  useEffect(() => {
    if (!notificationUserKey) return;
    if (notificationsUserKeyRef.current === notificationUserKey && notificationsHydrated) return;

    notificationsUserKeyRef.current = notificationUserKey;
    setNotificationsHydrated(false);

    try {
      const raw = localStorage.getItem(`miniapp_v2_notifications_shown:${notificationUserKey}`);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed)) {
        setShownNotificationKeys(new Set(parsed.filter((value: unknown) => typeof value === 'string')));
      } else {
        setShownNotificationKeys(new Set());
      }
    } catch {
      setShownNotificationKeys(new Set());
    }

    try {
      const rawLastStreak = localStorage.getItem(`miniapp_v2_last_streak:${notificationUserKey}`);
      const parsed = Number(rawLastStreak || 0);
      lastStreakNotificationRef.current = Number.isFinite(parsed) ? parsed : 0;
    } catch {
      lastStreakNotificationRef.current = 0;
    }

    setNotificationQueue([]);
    setActiveNotification(null);
    setNotificationLeaving(false);
    setSocialPromptQueue([]);
    setActiveSocialPrompt(null);
    setSocialPromptActionId(null);
    pendingNotificationIdsRef.current = new Set();
    setNotificationsHydrated(true);
  }, [notificationUserKey, notificationsHydrated]);

  useEffect(() => {
    if (!notificationUserKey || !notificationsHydrated) return;
    try {
      localStorage.setItem(
        `miniapp_v2_notifications_shown:${notificationUserKey}`,
        JSON.stringify(Array.from(shownNotificationKeys).slice(-300))
      );
    } catch {
      // noop
    }
  }, [notificationUserKey, notificationsHydrated, shownNotificationKeys]);

  const enqueueNotification = useCallback((notification: V2NotificationItem) => {
    if (!notificationsHydrated || !notification.id) return;
    if (shownNotificationKeys.has(notification.id)) return;
    if (pendingNotificationIdsRef.current.has(notification.id)) return;
    if (activeNotification?.id === notification.id) return;

    pendingNotificationIdsRef.current.add(notification.id);
    setShownNotificationKeys((current) => {
      const next = new Set(current);
      next.add(notification.id);
      return next;
    });

    setNotificationQueue((current) => {
      if (current.some((item) => item.id === notification.id)) {
        pendingNotificationIdsRef.current.delete(notification.id);
        return current;
      }
      return [...current, notification];
    });
  }, [activeNotification?.id, notificationsHydrated, shownNotificationKeys]);

  const dismissActiveNotification = useCallback((persist = true) => {
    if (!activeNotification || notificationLeaving) return;

    setNotificationLeaving(true);
    if (notificationAutoCloseRef.current != null) {
      window.clearTimeout(notificationAutoCloseRef.current);
      notificationAutoCloseRef.current = null;
    }

    notificationDismissRef.current = window.setTimeout(() => {
      setActiveNotification(null);
      setNotificationLeaving(false);
      notificationDismissRef.current = null;
    }, 180);
  }, [activeNotification, notificationLeaving]);

  useEffect(() => {
    if (activeNotification || notificationQueue.length === 0) return;
    const nextNotification = notificationQueue[0];
    setActiveNotification(nextNotification);
    setNotificationQueue((current) => current.slice(1));
    setNotificationLeaving(false);
  }, [activeNotification, notificationQueue]);

  useEffect(() => {
    if (activeSocialPrompt || socialPromptQueue.length === 0) return;
    const nextPrompt = socialPromptQueue[0];
    setActiveSocialPrompt(nextPrompt);
    setSocialPromptQueue((current) => current.slice(1));
  }, [activeSocialPrompt, socialPromptQueue]);

  useEffect(() => {
    if (!activeNotification) return;
    if (notificationAutoCloseRef.current != null) {
      window.clearTimeout(notificationAutoCloseRef.current);
    }
    notificationAutoCloseRef.current = window.setTimeout(() => {
      dismissActiveNotification(true);
    }, activeNotification.autoCloseMs ?? 5200);

    return () => {
      if (notificationAutoCloseRef.current != null) {
        window.clearTimeout(notificationAutoCloseRef.current);
        notificationAutoCloseRef.current = null;
      }
    };
  }, [activeNotification, dismissActiveNotification]);

  useEffect(() => {
    return () => {
      if (notificationAutoCloseRef.current != null) {
        window.clearTimeout(notificationAutoCloseRef.current);
      }
      if (notificationDismissRef.current != null) {
        window.clearTimeout(notificationDismissRef.current);
      }
      if (weekTransitionResetRef.current != null) {
        window.clearTimeout(weekTransitionResetRef.current);
      }
    };
  }, []);

  const buildMealNotificationSnapshot = useCallback((args: {
    meals: V2Meal[];
    stats: MiniAppStats | null;
    profileData: MiniAppProfile | null;
    activitySummary: { totalCalories: number; totalDuration: number; count: number } | null;
    caloriesMode: 'add_to_norm' | 'show_separate';
  }): MealNotificationSnapshot => {
    const consumed = roundNumber(args.stats?.totals?.calories ?? args.profileData?.today?.consumed);
    const baseTarget = roundNumber(args.stats?.targets?.calories ?? args.profileData?.targets?.calories ?? args.profileData?.today?.target);
    const activityBonus = args.caloriesMode === 'add_to_norm' ? roundNumber(args.activitySummary?.totalCalories) : 0;
    const target = baseTarget + activityBonus;
    const calorieMilestone = classifyCalorieMilestone(consumed, target);
    return {
      mealsCount: args.meals.length,
      caloriesConsumed: consumed,
      caloriesTarget: target,
      calorieStatus: calorieMilestone.status,
      calorieLowerBound: calorieMilestone.lowerBound,
      calorieUpperBound: calorieMilestone.upperBound,
      caloriesToRange: calorieMilestone.remainingToRange,
      isInCalorieTarget: calorieMilestone.status === 'in_range',
    };
  }, []);

  const loadData = useCallback(async (date: Date, options?: { silent?: boolean; preferCache?: boolean }): Promise<HomeLoadResult | null> => {
    const requestId = ++loadDataRequestIdRef.current;
    const isStaleRequest = () => loadDataRequestIdRef.current !== requestId;
    const silent = options?.silent === true;
    const dateKey = formatLocalDate(date);
    const cacheKey = `home:${dateKey}`;
    const cached = options?.preferCache === false ? null : readV2SessionCache<{
      profileData: MiniAppProfile;
      statsData: MiniAppStats;
      mealsData: { meals: V2Meal[] };
      activitiesData: any;
      waterData: MiniAppWaterSummary | null;
      tipsPrompt: string;
    }>(cacheKey);

	    if (cached) {
	      setProfile(cached.profileData);
	      setDayStats(cached.statsData);
	      setDayMeals((cached.mealsData?.meals || []) as V2Meal[]);
	      setHomeDataDateKey(dateKey);
	      setHomeActivitySummary(cached.activitiesData ? {
        totalCalories: roundNumber(cached.activitiesData?.summary?.totalCalories),
        totalDuration: roundNumber(cached.activitiesData?.summary?.totalDuration),
        count: roundNumber(cached.activitiesData?.summary?.count),
      } : null);
      setActivityCaloriesMode((cached.activitiesData?.caloriesMode as 'add_to_norm' | 'show_separate') || 'show_separate');
      setWaterSummary(cached.waterData || null);
      setTipsPrompt(cached.tipsPrompt || 'Сгенерировать подсказку');
      setIsLoading(false);
    }

    if (silent) setIsRefreshing(true);
    else if (!cached) setIsLoading(true);

    setError(null);

    try {
      await ensureMiniAppAuth();
      if (isStaleRequest()) return null;
      if (api.getMiniAppOnboardingRequired()) {
        setOnboardingRequired(true);
	        setProfile(null);
	        setDayStats(null);
	        setDayMeals([]);
	        setHomeDataDateKey(null);
	        setWaterSummary(null);
	        return null;
      }

      const isSelectedToday = isSameDay(date, today);

      const [profileData, statsData, mealsData, activitiesData, waterData] = await Promise.all([
        api.getMiniAppProfile(),
        api.getMiniAppStats('day', dateKey),
        api.getMiniAppMeals(dateKey),
        api.getActivities(dateKey).catch(() => null),
        api.getWater(dateKey).catch(() => null),
      ]);

      if (isStaleRequest()) {
        // Более новый вызов loadData уже в работе/завершён — не перезатираем его данными
        // за этот (более старый) день, но результат всё равно возвращаем явному await-вызову.
        return { profileData, statsData, mealsData: { meals: (mealsData?.meals || []) as V2Meal[] }, activitiesData, waterData };
      }

	      setProfile(profileData);
	      setDayStats(statsData);
	      setDayMeals((mealsData?.meals || []) as V2Meal[]);
	      setHomeDataDateKey(dateKey);
	      setHomeActivitySummary(activitiesData ? {
        totalCalories: roundNumber(activitiesData?.summary?.totalCalories),
        totalDuration: roundNumber(activitiesData?.summary?.totalDuration),
        count: roundNumber(activitiesData?.summary?.count),
      } : null);
      setActivityCaloriesMode((activitiesData?.caloriesMode as 'add_to_norm' | 'show_separate') || 'show_separate');
      setWaterSummary(waterData);
      let nextTipsPrompt = 'Сгенерировать подсказку';

      if (isSelectedToday) {
        try {
          const adviceStatus = await api.getMiniAppAdviceStatus();
          nextTipsPrompt = adviceStatus?.hasAdvice
            ? 'Посмотреть подсказку'
            : 'Сгенерировать подсказку';
          setTipsPrompt(nextTipsPrompt);
        } catch {
          nextTipsPrompt = 'Сгенерировать подсказку';
          setTipsPrompt('Сгенерировать подсказку');
        }
      } else {
        nextTipsPrompt = 'Советы доступны только на сегодня';
        setTipsPrompt('Советы доступны только на сегодня');
      }

      writeV2SessionCache(cacheKey, {
        profileData,
        statsData,
        mealsData,
        activitiesData,
        waterData,
        tipsPrompt: nextTipsPrompt,
      }, 60 * 1000);
      return { profileData, statsData, mealsData: { meals: (mealsData?.meals || []) as V2Meal[] }, activitiesData, waterData };
    } catch (loadError: any) {
      if (isStaleRequest()) return null;
      if (String(loadError?.code || '') === 'ONBOARDING_REQUIRED' || api.getMiniAppOnboardingRequired()) {
        setOnboardingRequired(true);
	        setProfile(null);
	        setDayStats(null);
	        setDayMeals([]);
	        setHomeDataDateKey(null);
	        setWaterSummary(null);
	        return null;
      }

      console.error('Failed to load v2 miniapp data', loadError);
      setError(loadError?.message || 'Не получилось загрузить данные');
      return null;
    } finally {
      if (!isStaleRequest()) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [api, ensureMiniAppAuth, today]);

  useEffect(() => {
    loadData(selectedDate);
  }, [loadData, selectedDate]);

  useEffect(() => {
    if (!isSameDay(selectedDate, today)) {
      setTipsModalOpen(false);
      setTipsModalLoading(false);
    }
  }, [selectedDate, today]);

  useEffect(() => {
    const expectedWeekStart = startOfWeek(selectedDate);
    if (!isSameDay(expectedWeekStart, weekStartDate)) {
      setWeekStartDate(expectedWeekStart);
    }
  }, [selectedDate, weekStartDate]);

  const currentWeekDates = useMemo(() => getWeekDates(weekStartDate), [weekStartDate]);

  const loadWeekActivitySnapshots = useCallback(async (anchorDate: Date): Promise<ActivityDaySnapshot[]> => {
    const weekKey = formatLocalDate(startOfWeek(anchorDate));
    const cached = readV2SessionCache<ActivityDaySnapshot[]>(`activity-week:${weekKey}`);
    if (cached) {
      return cached;
    }

    await ensureMiniAppAuth();
    const dates = getWeekDates(anchorDate);

    const responses = await Promise.all(
      dates.map(async (date) => {
        const dateKey = formatLocalDate(date);
        if (date.getTime() > today.getTime()) {
          return { dateKey, totalCalories: 0, totalDuration: 0, count: 0, activities: [] };
        }

        try {
          const result = await api.getActivities(dateKey);
          return {
            dateKey,
            totalCalories: roundNumber(result?.summary?.totalCalories),
            totalDuration: roundNumber(result?.summary?.totalDuration),
            count: roundNumber(result?.summary?.count),
            activities: result?.activities || [],
          };
        } catch {
          return { dateKey, totalCalories: 0, totalDuration: 0, count: 0, activities: [] };
        }
      })
    );

    writeV2SessionCache(`activity-week:${weekKey}`, responses, 2 * 60 * 1000);
    return responses;
  }, [api, ensureMiniAppAuth, today]);

  useEffect(() => {
    let cancelled = false;

    const loadWeekMarkers = async () => {
      try {
        await ensureMiniAppAuth();
        const cacheKey = formatLocalDate(weekStartDate);
        const cached = weekMarkersCacheRef.current.get(cacheKey);
        if (cached) {
          if (!cancelled) {
            setWeekMealDays(new Set(cached));
          }
          return;
        }

        const persisted = readV2SessionCache<string[]>(`week-markers:${cacheKey}`);
        if (persisted) {
          const nextMarkers = new Set(persisted);
          weekMarkersCacheRef.current.set(cacheKey, nextMarkers);
          if (!cancelled) {
            setWeekMealDays(nextMarkers);
          }
          return;
        }

        const markers = new Set<string>();
        const visibleDates = currentWeekDates.filter((date) => date.getTime() <= today.getTime());
        const responses = await Promise.all(
          visibleDates.map(async (date) => {
            const dateKey = formatLocalDate(date);
            try {
              const result = await api.getMiniAppMeals(dateKey, 1, 0);
              return { dateKey, hasMeal: Array.isArray(result?.meals) && result.meals.length > 0 };
            } catch {
              return { dateKey, hasMeal: false };
            }
          })
        );

        responses.forEach(({ dateKey, hasMeal }) => {
          if (hasMeal) markers.add(dateKey);
        });

        weekMarkersCacheRef.current.set(cacheKey, markers);
        writeV2SessionCache(`week-markers:${cacheKey}`, Array.from(markers), 10 * 60 * 1000);
        if (!cancelled) {
          setWeekMealDays(new Set(markers));
        }
      } catch {
        if (!cancelled) {
          setWeekMealDays(new Set());
        }
      }
    };

    loadWeekMarkers();
    return () => {
      cancelled = true;
    };
  }, [api, currentWeekDates, ensureMiniAppAuth, today, weekStartDate]);

  const loadStatisticsView = useCallback(async (date: Date, options?: { preferCache?: boolean }) => {
    const dateKey = formatLocalDate(date);
    const cacheKey = `statistics:${dateKey}`;
    const cached = options?.preferCache === false ? null : readV2SessionCache<{
      stats: MiniAppStats;
      weekActivities: ActivityDaySnapshot[];
    }>(cacheKey);

    if (cached) {
      setWeekStatsView(cached.stats);
      setWeekActivitySeries(cached.weekActivities);
      setStatsViewLoading(false);
    } else {
      setStatsViewLoading(true);
    }
    try {
      await ensureMiniAppAuth();
      const [stats, weekActivities] = await Promise.all([
        api.getMiniAppStats('week', dateKey),
        loadWeekActivitySnapshots(date),
      ]);
      setWeekStatsView(stats);
      setWeekActivitySeries(weekActivities);
      writeV2SessionCache(cacheKey, { stats, weekActivities }, 2 * 60 * 1000);
    } catch (viewError) {
      console.error('Failed to load statistics v2 view', viewError);
      setWeekStatsView(null);
      setWeekActivitySeries([]);
    } finally {
      setStatsViewLoading(false);
    }
  }, [api, ensureMiniAppAuth, loadWeekActivitySnapshots]);

  const loadActivityView = useCallback(async (date: Date, options?: { preferCache?: boolean }) => {
    const dateKey = formatLocalDate(date);
    const cacheKey = `activity:${dateKey}`;
    const cached = options?.preferCache === false ? null : readV2SessionCache<{
      activityDay: ActivityDaySnapshot;
      weekActivities: ActivityDaySnapshot[];
      caloriesMode: 'add_to_norm' | 'show_separate';
    }>(cacheKey);

    if (cached) {
      setActivityDay(cached.activityDay);
      setWeekActivitySeries(cached.weekActivities);
      setActivityCaloriesMode(cached.caloriesMode);
      setActivityViewLoading(false);
    } else {
      setActivityViewLoading(true);
    }
    try {
      await ensureMiniAppAuth();
      const [dailyActivities, weekActivities] = await Promise.all([
        api.getActivities(dateKey),
        loadWeekActivitySnapshots(date),
      ]);
      setActivityCaloriesMode((dailyActivities?.caloriesMode as 'add_to_norm' | 'show_separate') || 'show_separate');
      setActivityDay({
        dateKey,
        totalCalories: roundNumber(dailyActivities?.summary?.totalCalories),
        totalDuration: roundNumber(dailyActivities?.summary?.totalDuration),
        count: roundNumber(dailyActivities?.summary?.count),
        activities: dailyActivities?.activities || [],
      });
      setWeekActivitySeries(weekActivities);
      writeV2SessionCache(cacheKey, {
        activityDay: {
          dateKey,
          totalCalories: roundNumber(dailyActivities?.summary?.totalCalories),
          totalDuration: roundNumber(dailyActivities?.summary?.totalDuration),
          count: roundNumber(dailyActivities?.summary?.count),
          activities: dailyActivities?.activities || [],
        },
        weekActivities,
        caloriesMode: (dailyActivities?.caloriesMode as 'add_to_norm' | 'show_separate') || 'show_separate',
      }, 2 * 60 * 1000);
    } catch (viewError) {
      console.error('Failed to load activity v2 view', viewError);
      setActivityDay(null);
      setWeekActivitySeries([]);
    } finally {
      setActivityViewLoading(false);
    }
  }, [api, ensureMiniAppAuth, loadWeekActivitySnapshots]);

  const loadSocialView = useCallback(async (options?: { preferCache?: boolean }) => {
    const cacheKey = 'social:overview';
    const cached = options?.preferCache === false ? null : readV2SessionCache<SocialOverview>(cacheKey);
    if (cached) {
      setSocialOverview(cached);
      setSocialViewLoading(false);
    } else {
      setSocialViewLoading(true);
    }
    try {
      await ensureMiniAppAuth();
      const overview = await api.getSocialOverview();
      setSocialOverview(overview);
      writeV2SessionCache(cacheKey, overview, 2 * 60 * 1000);
    } catch (viewError) {
      console.error('Failed to load social v2 view', viewError);
      setSocialOverview(null);
    } finally {
      setSocialViewLoading(false);
    }
  }, [api, ensureMiniAppAuth]);

  const loadSocialPrompts = useCallback(async () => {
    try {
      await ensureMiniAppAuth();
      const prompts = await api.getSocialPrompts();
      setSocialPromptQueue(prompts || []);
      setActiveSocialPrompt(null);
      setSocialPromptActionId(null);
    } catch (promptError) {
      console.error('Failed to load social prompts', promptError);
      setSocialPromptQueue([]);
      setActiveSocialPrompt(null);
      setSocialPromptActionId(null);
    }
  }, [api, ensureMiniAppAuth]);

  const loadAchievementsView = useCallback(async (options?: { preferCache?: boolean }) => {
    const cacheKey = 'achievements:overview';
    const cached = options?.preferCache === false ? null : readV2SessionCache<AchievementsOverview>(cacheKey);
    if (cached) {
      setAchievementsOverview(cached);
      setAchievementsLoading(false);
      setAchievementsError(null);
    } else {
      setAchievementsLoading(true);
    }
    setAchievementsError(null);
    try {
      await ensureMiniAppAuth();
      const overview = await api.getAchievements();
      setAchievementsOverview(overview);
      writeV2SessionCache(cacheKey, overview, 2 * 60 * 1000);
    } catch (viewError: any) {
      console.error('Failed to load achievements v2 view', viewError);
      setAchievementsError(viewError?.message || 'Не получилось загрузить достижения');
      setAchievementsOverview(null);
    } finally {
      setAchievementsLoading(false);
    }
  }, [api, ensureMiniAppAuth]);

  const handleRequestFriend = useCallback(async (username: string) => {
    await ensureMiniAppAuth();
    const result = await api.requestFriend(username);
    clearV2SessionCacheByPrefix('social:');
    await loadSocialView({ preferCache: false });
    return result;
  }, [api, ensureMiniAppAuth, loadSocialView]);

  const handleAcceptFriendRequest = useCallback(async (friendshipId: string) => {
    await ensureMiniAppAuth();
    await api.acceptFriendRequest(friendshipId);
    clearV2SessionCacheByPrefix('social:');
    clearV2SessionCacheByPrefix('achievements:');
    await loadSocialView({ preferCache: false });
  }, [api, ensureMiniAppAuth, loadSocialView]);

  const handleDeclineFriendRequest = useCallback(async (friendshipId: string) => {
    await ensureMiniAppAuth();
    await api.declineFriendRequest(friendshipId);
    clearV2SessionCacheByPrefix('social:');
    await loadSocialView({ preferCache: false });
  }, [api, ensureMiniAppAuth, loadSocialView]);

  const handleRemoveFriend = useCallback(async (friendUserId: string) => {
    await ensureMiniAppAuth();
    await api.removeFriend(friendUserId);
    clearV2SessionCacheByPrefix('social:');
    clearV2SessionCacheByPrefix('achievements:');
    await loadSocialView({ preferCache: false });
  }, [api, ensureMiniAppAuth, loadSocialView]);

  const handleCreateFriendChallenge = useCallback(async (friendUserId: string, goalType: FriendChallengeGoalType) => {
    await ensureMiniAppAuth();
    await api.createFriendChallenge(friendUserId, goalType);
    clearV2SessionCacheByPrefix('social:');
    clearV2SessionCacheByPrefix('achievements:');
    await Promise.allSettled([
      loadSocialView({ preferCache: false }),
      loadAchievementsView(),
    ]);
  }, [api, ensureMiniAppAuth, loadAchievementsView, loadSocialView]);

  const handleAcceptFriendChallenge = useCallback(async (challengeId: string) => {
    await ensureMiniAppAuth();
    await api.acceptFriendChallenge(challengeId);
    clearV2SessionCacheByPrefix('social:');
    clearV2SessionCacheByPrefix('achievements:');
    await Promise.allSettled([
      loadSocialView({ preferCache: false }),
      loadAchievementsView(),
    ]);
  }, [api, ensureMiniAppAuth, loadAchievementsView, loadSocialView]);

  const handleDeclineFriendChallenge = useCallback(async (challengeId: string) => {
    await ensureMiniAppAuth();
    await api.declineFriendChallenge(challengeId);
    clearV2SessionCacheByPrefix('social:');
    await loadSocialView({ preferCache: false });
  }, [api, ensureMiniAppAuth, loadSocialView]);

  const handleSendFriendChallengeNudge = useCallback(async (challengeId: string) => {
    await ensureMiniAppAuth();
    await api.sendFriendChallengeNudge(challengeId);
    clearV2SessionCacheByPrefix('social:');
    clearV2SessionCacheByPrefix('achievements:');
    await Promise.allSettled([
      loadSocialView({ preferCache: false }),
      loadAchievementsView(),
    ]);
  }, [api, ensureMiniAppAuth, loadAchievementsView, loadSocialView]);

  const handleCancelFriendChallenge = useCallback(async (challengeId: string) => {
    await ensureMiniAppAuth();
    await api.cancelFriendChallenge(challengeId);
    clearV2SessionCacheByPrefix('social:');
    clearV2SessionCacheByPrefix('achievements:');
    await Promise.allSettled([
      loadSocialView({ preferCache: false }),
      loadAchievementsView(),
    ]);
  }, [api, ensureMiniAppAuth, loadAchievementsView, loadSocialView]);

  const handleUpdateSocialPrivacy = useCallback(async (data: Partial<SocialOverview['privacy']>) => {
    await ensureMiniAppAuth();
    const updated = await api.updateSocialPrivacy(data);
    setSocialOverview((prev) => (prev ? { ...prev, privacy: updated } : prev));
    return updated;
  }, [api, ensureMiniAppAuth]);

  const closeActiveSocialPrompt = useCallback(() => {
    setActiveSocialPrompt(null);
    setSocialPromptActionId(null);
  }, []);

  const handleDismissSocialPrompt = useCallback(async () => {
    if (!activeSocialPrompt) return;
    try {
      setSocialPromptActionId(activeSocialPrompt.id);
      await ensureMiniAppAuth();
      await api.dismissSocialPrompt(activeSocialPrompt.id);
      closeActiveSocialPrompt();
    } catch (promptError) {
      console.error('Failed to dismiss social prompt', promptError);
      setSocialPromptActionId(null);
    }
  }, [activeSocialPrompt, api, closeActiveSocialPrompt, ensureMiniAppAuth]);

  const handleCongratulateSocialPrompt = useCallback(async () => {
    if (!activeSocialPrompt) return;
    try {
      setSocialPromptActionId(activeSocialPrompt.id);
      await ensureMiniAppAuth();
      await api.congratulateSocialPrompt(activeSocialPrompt.id);
      enqueueNotification({
        id: `social-congrats:${activeSocialPrompt.id}`,
        kind: 'achievement',
        title: 'Поддержка отправлена',
        message: `Сообщение для ${formatDisplayName(activeSocialPrompt.friend.firstName, activeSocialPrompt.friend.username)} уже в пути.`,
        actionLabel: 'Хорошо',
        autoCloseMs: 3200,
        emojiIcon: '👏',
      });
      closeActiveSocialPrompt();
    } catch (promptError) {
      console.error('Failed to send social congrats', promptError);
      setSocialPromptActionId(null);
    }
  }, [activeSocialPrompt, api, closeActiveSocialPrompt, enqueueNotification, ensureMiniAppAuth]);

  const handleActivityCreated = useCallback(async () => {
    clearV2SessionCacheByPrefix(`home:${formatLocalDate(selectedDate)}`);
    clearV2SessionCacheByPrefix(`activity:${formatLocalDate(selectedDate)}`);
    clearV2SessionCacheByPrefix(`statistics:${formatLocalDate(selectedDate)}`);
    clearV2SessionCacheByPrefix(`activity-week:${formatLocalDate(startOfWeek(selectedDate))}`);
    clearV2SessionCacheByPrefix('achievements:');
    await Promise.allSettled([
      loadActivityView(selectedDate, { preferCache: false }),
      loadStatisticsView(selectedDate, { preferCache: false }),
      loadData(selectedDate, { silent: true, preferCache: false }),
    ]);
  }, [loadActivityView, loadData, loadStatisticsView, selectedDate]);

  const handleAddWater = useCallback(async (amountMl: number) => {
    if (waterSaving) return;
    const dateKey = formatLocalDate(selectedDate);
    const beforeTotal = roundNumber(waterSummary?.totalMl);
    const target = roundNumber(waterSummary?.targetMl || Math.max(1500, Math.min(3500, Math.round(((Number(profile?.profile?.weight) || 70) * 30) / 100) * 100)));
    setWaterSaving(true);
    try {
      await ensureMiniAppAuth();
      await api.addWater(amountMl);
      clearV2SessionCacheByPrefix(`home:${dateKey}`);
      const nextWater = await api.getWater(dateKey);
      setWaterSummary(nextWater);
      setWaterSplashKey((current) => current + 1);
      if (isSameDay(selectedDate, today) && target > 0 && beforeTotal < target && Number(nextWater?.totalMl || 0) >= target) {
        enqueueNotification({
          id: `water-goal:${dateKey}:${nextWater.totalMl}`,
          kind: 'achievement',
          title: 'Вода закрыта',
          message: `${nextWater.totalMl} мл из ${target} мл. День стал чуть ровнее.`,
          actionLabel: 'Отлично',
          autoCloseMs: 4200,
          emojiIcon: '💧',
        });
      }
    } catch (waterError) {
      console.error('Failed to add water', waterError);
    } finally {
      setWaterSaving(false);
    }
  }, [api, enqueueNotification, ensureMiniAppAuth, profile?.profile?.weight, selectedDate, today, waterSaving, waterSummary?.targetMl, waterSummary?.totalMl]);

  const handleActivityCaloriesModeChange = useCallback(async (mode: 'add_to_norm' | 'show_separate') => {
    await ensureMiniAppAuth();
    await api.setCaloriesMode(mode);
    setActivityCaloriesMode(mode);
  }, [api, ensureMiniAppAuth]);

  useEffect(() => {
    if (activeTab === 'statistics') {
      loadStatisticsView(selectedDate);
    }
  }, [activeTab, loadStatisticsView, selectedDate]);

  useEffect(() => {
    if (activeTab === 'activity') {
      loadActivityView(selectedDate);
    }
  }, [activeTab, loadActivityView, selectedDate]);

  useEffect(() => {
    if (activeTab === 'social') {
      loadSocialView();
    }
  }, [activeTab, loadSocialView]);

  useEffect(() => {
    void loadSocialPrompts();
  }, [loadSocialPrompts]);

  useEffect(() => {
    if (standaloneScreen?.type === 'profile' && !socialOverview && !socialViewLoading) {
      void loadSocialView();
    }
  }, [loadSocialView, socialOverview, socialViewLoading, standaloneScreen]);

  useEffect(() => {
    if (standaloneScreen?.type === 'profile' && standaloneScreen.initialView === 'achievements' && !achievementsOverview && !achievementsLoading) {
      void loadAchievementsView();
    }
  }, [achievementsLoading, achievementsOverview, loadAchievementsView, standaloneScreen]);

  useEffect(() => {
    if (standaloneScreen?.type === 'streak' && !achievementsOverview && !achievementsLoading) {
      void loadAchievementsView();
    }
  }, [achievementsLoading, achievementsOverview, loadAchievementsView, standaloneScreen]);

  useEffect(() => {
    if (!notificationsHydrated || achievementsOverview || achievementsLoading || !profile) return;
    void loadAchievementsView();
  }, [achievementsLoading, achievementsOverview, loadAchievementsView, notificationsHydrated, profile]);

  const mealSections = useMemo(() => {
    const groups = new Map<string, { category: string; totalCalories: number; meals: V2Meal[] }>();

    dayMeals.forEach((meal) => {
      const existing = groups.get(meal.category) || {
        category: meal.category,
        totalCalories: 0,
        meals: [],
      };
      existing.totalCalories += Number(meal.calories || 0);
      existing.meals.push(meal);
      groups.set(meal.category, existing);
    });

    return mealTypeDefinitions.map((mealType) => {
      const group = groups.get(mealType.id);

      return {
        category: mealType.id,
        label: mealType.label,
        totalCalories: roundNumber(group?.totalCalories),
        meals: (group?.meals || []).sort(
          (left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()
        ),
      };
    });
  }, [dayMeals]);

  const streakDays = roundNumber(profile?.stats?.streak);
  const caloriesConsumed = roundNumber(dayStats?.totals?.calories ?? profile?.today?.consumed);
  const baseCaloriesTarget = roundNumber(dayStats?.targets?.calories ?? profile?.targets?.calories ?? profile?.today?.target);
  const activityCaloriesBonus = activityCaloriesMode === 'add_to_norm' ? roundNumber(homeActivitySummary?.totalCalories) : 0;
  const caloriesTarget = baseCaloriesTarget + activityCaloriesBonus;
  const caloriesRemaining = Math.max(0, roundNumber(caloriesTarget - (dayStats?.totals?.calories ?? profile?.today?.consumed ?? 0)));
  const caloriesProgress = clampPercent(caloriesConsumed, caloriesTarget);
  const selectedDateTitle = formatLongDate(formatLocalDate(selectedDate));
  const canGoToNextWeek = weekStartDate.getTime() < todayWeekStart.getTime();
	  const activityGoal = getActivityGoalByLevel(profile?.profile?.activityLevel);
	  const isTodaySelected = isSameDay(selectedDate, today);
	  const selectedDateKey = formatLocalDate(selectedDate);
	  const todayKey = formatLocalDate(today);
	  const isLiveNotificationDataCurrent = isTodaySelected && homeDataDateKey === selectedDateKey && selectedDateKey === todayKey;
	  const fallbackWaterTarget = Math.max(1500, Math.min(3500, Math.round(((Number(profile?.profile?.weight) || 70) * 30) / 100) * 100));
  const waterTotalMl = roundNumber(waterSummary?.totalMl);
  const waterTargetMl = roundNumber(waterSummary?.targetMl || fallbackWaterTarget);
	  const canShowLiveNotifications = notificationsHydrated && isLiveNotificationDataCurrent;
  const currentMealNotificationSnapshot = useMemo(() => buildMealNotificationSnapshot({
    meals: dayMeals,
    stats: dayStats,
    profileData: profile,
    activitySummary: homeActivitySummary,
    caloriesMode: activityCaloriesMode,
  }), [activityCaloriesMode, buildMealNotificationSnapshot, dayMeals, dayStats, homeActivitySummary, profile]);
  const coursePlan = profile?.profile?.plan || null;
  const { title: courseTitle, actualRate: courseActualRate, mascotSrc: courseMascotSrc } = getCoursePresentation(coursePlan);

  const enqueueMealChangeNotifications = useCallback((before: MealNotificationSnapshot, after: MealNotificationSnapshot) => {
    if (!canShowLiveNotifications) return;
    const todayKey = formatLocalDate(today);

    if (before.mealsCount === 0 && after.mealsCount > 0) {
      enqueueNotification({
        id: `first_meal:${todayKey}`,
        kind: 'first_meal',
        title: 'Первый приём пищи добавлен',
        message: 'День начат. Продолжай в том же ритме и отмечай остальные приёмы.',
        actionLabel: 'Супер',
        autoCloseMs: 4600,
      });
    }

    if (!before.isInCalorieTarget && after.isInCalorieTarget) {
      enqueueNotification({
        id: `calorie_target:${todayKey}`,
        kind: 'calorie_target',
        title: 'Ты в норме по калориям',
        message: `${after.caloriesConsumed} из ${after.caloriesTarget} ккал. Диапазон дня: ${after.calorieLowerBound}-${after.calorieUpperBound} ккал.`,
        actionLabel: 'Отлично',
        autoCloseMs: 5000,
      });
    } else if (before.calorieStatus === 'none' && after.calorieStatus === 'near_lower') {
      enqueueNotification({
        id: `calorie_near:${todayKey}`,
        kind: 'calorie_target',
        title: 'Почти в идеале',
        message: `Осталось ${after.caloriesToRange} ккал до диапазона нормы.`,
        actionLabel: 'Понял',
        autoCloseMs: 5000,
      });
    }
  }, [canShowLiveNotifications, enqueueNotification, today]);

  useEffect(() => {
    if (!canShowLiveNotifications) return;
    if (currentMealNotificationSnapshot.mealsCount <= 0) return;

    const todayKey = formatLocalDate(today);
    if (currentMealNotificationSnapshot.calorieStatus === 'in_range') {
      enqueueNotification({
        id: `calorie_target:${todayKey}`,
        kind: 'calorie_target',
        title: 'Ты в норме по калориям',
        message: `${currentMealNotificationSnapshot.caloriesConsumed} из ${currentMealNotificationSnapshot.caloriesTarget} ккал. Диапазон дня: ${currentMealNotificationSnapshot.calorieLowerBound}-${currentMealNotificationSnapshot.calorieUpperBound} ккал.`,
        actionLabel: 'Отлично',
        autoCloseMs: 5000,
      });
    } else if (currentMealNotificationSnapshot.calorieStatus === 'near_lower') {
      enqueueNotification({
        id: `calorie_near:${todayKey}`,
        kind: 'calorie_target',
        title: 'Почти в идеале',
        message: `Осталось ${currentMealNotificationSnapshot.caloriesToRange} ккал до диапазона нормы.`,
        actionLabel: 'Понял',
        autoCloseMs: 5000,
      });
    }
  }, [canShowLiveNotifications, currentMealNotificationSnapshot, enqueueNotification, today]);

  const macroValues = {
    protein: {
      current: roundNumber(dayStats?.totals?.protein ?? profile?.today?.protein),
      target: roundNumber(dayStats?.targets?.protein ?? profile?.targets?.protein),
    },
    fats: {
      current: roundNumber(dayStats?.totals?.fats ?? profile?.today?.fats),
      target: roundNumber(dayStats?.targets?.fats ?? profile?.targets?.fats),
    },
    carbs: {
      current: roundNumber(dayStats?.totals?.carbs ?? profile?.today?.carbs),
      target: roundNumber(dayStats?.targets?.carbs ?? profile?.targets?.carbs),
    },
  };

  useEffect(() => {
    if (isTodaySelected) return;

    if (notificationAutoCloseRef.current != null) {
      window.clearTimeout(notificationAutoCloseRef.current);
      notificationAutoCloseRef.current = null;
    }
    if (notificationDismissRef.current != null) {
      window.clearTimeout(notificationDismissRef.current);
      notificationDismissRef.current = null;
    }

    pendingNotificationIdsRef.current.clear();
    setNotificationQueue([]);
    setActiveNotification(null);
    setNotificationLeaving(false);
  }, [isTodaySelected]);

  useEffect(() => {
    if (!canShowLiveNotifications) return;
    const currentStreak = roundNumber(profile?.stats?.streak);
    const userKey = notificationsUserKeyRef.current;
    const todayKey = formatLocalDate(today);
    const streakMilestones = [3, 7, 14, 30, 60, 100];
    const previousStreak = Number.isFinite(lastStreakNotificationRef.current) ? lastStreakNotificationRef.current : 0;

    if (streakMilestones.includes(currentStreak) && currentStreak > previousStreak) {
      enqueueNotification({
        id: `streak:${todayKey}:${currentStreak}`,
        kind: 'streak',
        title: `${currentStreak} ${pluralize(currentStreak, ['день', 'дня', 'дней'])} подряд!`,
        message: 'Вы в огне, так держать 🔥',
        actionLabel: 'Понял',
        autoCloseMs: 5600,
        fireEffect: currentStreak >= 7,
      });
    }

    lastStreakNotificationRef.current = currentStreak;
    if (userKey) {
      try {
        localStorage.setItem(`miniapp_v2_last_streak:${userKey}`, String(currentStreak));
      } catch {
        // noop
      }
    }
  }, [canShowLiveNotifications, enqueueNotification, profile?.stats?.streak, today]);

  useEffect(() => {
    if (!canShowLiveNotifications || !achievementsOverview) return;

    const now = Date.now();
    const recentUnlocked = (achievementsOverview.groups || [])
      .flatMap((group) => group.items || [])
      .filter((item) => {
        if (!item.unlocked || !item.unlockedAt) return false;
        const unlockedAt = new Date(item.unlockedAt).getTime();
        if (!Number.isFinite(unlockedAt)) return false;
        return now - unlockedAt <= 36 * 60 * 60 * 1000;
      })
      .sort((left, right) => new Date(right.unlockedAt || 0).getTime() - new Date(left.unlockedAt || 0).getTime());

    recentUnlocked.forEach((item) => {
      enqueueNotification({
        id: `achievement:${item.key}:${item.unlockedAt}`,
        kind: 'achievement',
        title: item.title,
        message: item.description,
        actionLabel: 'Круто',
        autoCloseMs: 6200,
        achievementEffect: true,
        emojiIcon: item.icon || '🏆',
      });
    });
  }, [achievementsOverview, canShowLiveNotifications, enqueueNotification]);

  useEffect(() => {
    if (!canShowLiveNotifications || !notificationUserKey || !coursePlan || coursePlan.trendConfidence === 'low') return;
    if (!['on_track', 'slower', 'faster', 'near_goal', 'goal_reached'].includes(coursePlan.status)) return;
    const storageKey = `miniapp_v2_course_status:${notificationUserKey}`;
    let previous = '';
    try { previous = localStorage.getItem(storageKey) || ''; localStorage.setItem(storageKey, coursePlan.status); } catch { /* noop */ }
    if (!previous || previous === coursePlan.status) return;
    enqueueNotification({
      id: `course:${coursePlan.status}:${coursePlan.lastReviewedAt || formatLocalDate(today)}`,
      kind: 'weight_goal',
      title: coursePlan.statusLabel,
      message: coursePlan.statusText,
      actionLabel: 'Посмотреть',
      autoCloseMs: 6500,
      emojiIcon: coursePlan.status === 'goal_reached' ? '🏆' : coursePlan.status === 'on_track' ? '🍇' : '🧭',
    });
  }, [canShowLiveNotifications, coursePlan, enqueueNotification, notificationUserKey, today]);

  const handleOpenTips = async () => {
    if (!isSameDay(selectedDate, today)) return;

    if (tipsAdvice?.hasAdvice && tipsAdvice.nextRefreshAt && new Date(tipsAdvice.nextRefreshAt).getTime() > Date.now()) {
      setTipsModalOpen(true);
      return;
    }

    try {
      setTipsModalOpen(true);
      setTipsModalLoading(true);
      await ensureMiniAppAuth();
      const advice = await api.getMiniAppAdvice();
      if (advice?.hasAdvice) {
        setTipsAdvice(advice);
        setTipsPrompt('Посмотреть подсказку');
      } else {
        setTipsAdvice({
          hasAdvice: false,
          title: 'Подсказка недоступна',
          tips: [],
          generatedAt: new Date().toISOString(),
          nextRefreshAt: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
          localDate: formatLocalDate(today),
          key: 'advice-empty',
        });
        setTipsPrompt('Сгенерировать подсказку');
      }
    } catch {
      setTipsAdvice({
        hasAdvice: false,
        title: 'Подсказка недоступна',
        tips: [],
        generatedAt: new Date().toISOString(),
        nextRefreshAt: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
        localDate: formatLocalDate(today),
        key: 'advice-error',
      });
      setTipsPrompt('Сгенерировать подсказку');
    } finally {
      setTipsModalLoading(false);
    }
  };

  const selectDay = useCallback((date: Date) => {
    if (date.getTime() > today.getTime()) return;
    setSelectedDate(date);
  }, [today]);

  const shiftWeek = useCallback((direction: -1 | 1) => {
    if (direction === 1 && !canGoToNextWeek) return;

    const nextWeekStart = direction === 1
      ? (canGoToNextWeek ? addDays(weekStartDate, 7) : weekStartDate)
      : addDays(weekStartDate, -7);

    const normalizedNextWeekStart = nextWeekStart.getTime() > todayWeekStart.getTime()
      ? todayWeekStart
      : nextWeekStart;

    const currentOffset = Math.max(0, Math.min(6, getDayOffset(weekStartDate, selectedDate)));
    let nextSelectedDate = addDays(normalizedNextWeekStart, currentOffset);
    if (nextSelectedDate.getTime() > today.getTime()) {
      nextSelectedDate = new Date(today);
      nextSelectedDate.setHours(0, 0, 0, 0);
    }

    setWeekTransitionDirection(direction === 1 ? 'left' : 'right');
    if (weekTransitionResetRef.current != null) {
      window.clearTimeout(weekTransitionResetRef.current);
    }
    weekTransitionResetRef.current = window.setTimeout(() => {
      setWeekTransitionDirection(null);
      weekTransitionResetRef.current = null;
    }, 260);

    setWeekStartDate(normalizedNextWeekStart);
    setSelectedDate(nextSelectedDate);
  }, [canGoToNextWeek, selectedDate, today, todayWeekStart, weekStartDate]);

  const weekSwipeHandlers = useHorizontalSwipeGesture<HTMLDivElement>({
    enabled: true,
    onHorizontalEnd: ({ deltaX }) => {
      if (Math.abs(deltaX) < 36) return;
      if (deltaX < 0) shiftWeek(1);
      else shiftWeek(-1);
    },
  });

  const handleReturnToToday = useCallback(() => {
    const normalizedToday = new Date(today);
    normalizedToday.setHours(0, 0, 0, 0);
    setWeekTransitionDirection('left');
    if (weekTransitionResetRef.current != null) {
      window.clearTimeout(weekTransitionResetRef.current);
    }
    weekTransitionResetRef.current = window.setTimeout(() => {
      setWeekTransitionDirection(null);
      weekTransitionResetRef.current = null;
    }, 260);
    setWeekStartDate(todayWeekStart);
    setSelectedDate(normalizedToday);
  }, [today, todayWeekStart]);

  const tabs: Array<{ id: V2TabId; label: string; icon: React.ReactNode }> = [
    { id: 'home', label: 'Главная', icon: <Home className="h-5 w-5" /> },
    { id: 'statistics', label: 'Статистика', icon: <BarChart3 className="h-5 w-5" /> },
    { id: 'activity', label: 'Активность', icon: <HeartPulse className="h-5 w-5" /> },
    { id: 'social', label: 'Комьюнити', icon: <MessageCircle className="h-5 w-5" /> },
  ];

  const classicCoachSteps = [
    { tab: 'home' as V2TabId, icon: <Target />, eyebrow: 'Твой день', title: 'Весь баланс — на главной', text: 'Здесь видно дневную цель, сколько уже съедено и сколько калорий и БЖУ осталось.' },
    { tab: 'home' as V2TabId, icon: <Plus />, eyebrow: 'Добавление еды', title: 'Фото, текст или повтор', text: 'Нажми центральный плюс. Noma распознает блюдо, а состав и вес всегда можно поправить вручную.' },
    { tab: 'statistics' as V2TabId, icon: <BarChart3 />, eyebrow: 'Статистика', title: 'Смотри на динамику', text: 'Графики недели показывают питание и прогресс точнее, чем результат одного отдельного дня.' },
    { tab: 'activity' as V2TabId, icon: <HeartPulse />, eyebrow: 'Активность', title: 'Учитывай движение', text: 'Добавляй шаги и тренировки и выбирай, учитывать их в дневной норме или показывать отдельно.' },
    { tab: 'social' as V2TabId, icon: <MessageCircle />, eyebrow: 'Комьюнити', title: 'Прогресс вместе легче', text: 'Добавляй друзей, поддерживай их реакциями и запускай совместные челленджи.' },
  ];
  const emotionalCoachSteps = [
    { tab: 'home' as V2TabId, icon: <Target />, eyebrow: 'Твой ориентир', title: 'Начни без требования быть идеальным', text: 'На главной видно дневной баланс. Один приём или день не определяет результат — важна общая динамика.' },
    { tab: 'home' as V2TabId, icon: <Plus />, eyebrow: 'Первое действие', title: 'Добавь еду за несколько секунд', text: 'Фото, текст или повтор блюда — Noma оценит состав, а вес и ингредиенты можно уточнить вручную.' },
    { tab: 'statistics' as V2TabId, icon: <BarChart3 />, eyebrow: 'Твой прогресс', title: 'Всё важное уже рядом', text: 'Внизу находятся статистика, активность и комьюнити. Открывай их по мере необходимости — запоминать всё сейчас не нужно.' },
  ];
  const coachSteps = onboardingVariant === 'emotional' ? emotionalCoachSteps : classicCoachSteps;
  const activeCoachStep = onboardingCoachStep === null ? null : coachSteps[onboardingCoachStep] || coachSteps[0];
  const activeCoachIndex = Math.min(onboardingCoachStep ?? 0, coachSteps.length - 1);

  useEffect(() => {
    if (!activeCoachStep) return;
    switchTab(activeCoachStep.tab);
    try { localStorage.setItem('noma_v2_onboarding_coach_progress', String(onboardingCoachStep)); } catch { /* noop */ }
  }, [activeCoachStep?.tab, onboardingCoachStep, switchTab]);

  const finishOnboardingCoach = useCallback(() => {
    try {
      localStorage.removeItem('noma_v2_onboarding_coach_pending');
      localStorage.removeItem('noma_v2_onboarding_coach_progress');
      localStorage.setItem('noma_v2_onboarding_coach_version', '3');
    } catch { /* noop */ }
    setOnboardingCoachStep(null);
    switchTab('home');
  }, [switchTab]);

  const restartOnboardingCoach = useCallback(() => {
    try {
      localStorage.setItem('noma_v2_onboarding_coach_pending', '3');
      localStorage.setItem('noma_v2_onboarding_coach_progress', '0');
    } catch { /* noop */ }
    setStandaloneScreen(null);
    setOnboardingCoachStep(0);
    switchTab('home');
  }, [switchTab]);

  const replaceMealInState = useCallback((updatedMeal: V2Meal) => {
    setDayMeals((current) => current.map((item) => (item.id === updatedMeal.id ? updatedMeal : item)));
    setSelectedMeal((current) => (current?.id === updatedMeal.id ? updatedMeal : current));
    setMealActionTarget((current) => (current?.id === updatedMeal.id ? updatedMeal : current));
    setMealPendingPortion((current) => (current?.id === updatedMeal.id ? updatedMeal : current));
  }, []);

  const openMealActions = useCallback((meal: V2Meal) => {
    if (deletingMealId || updatingMealPortionId) return;
    setMealActionTarget(meal);
  }, [deletingMealId, updatingMealPortionId]);

  const openMealPortionSheet = useCallback((meal: V2Meal) => {
    setMealActionTarget(null);
    setMealPendingPortion(meal);
    setMealConsumedDraft(getMealConsumedPercent(meal));
  }, []);

  const requestDeleteMeal = useCallback((meal: V2Meal) => {
    if (deletingMealId || updatingMealPortionId) return;
    setMealActionTarget(null);
    setMealPendingPortion(null);
    setMealPendingDelete(meal);
  }, [deletingMealId, updatingMealPortionId]);

  const handleDeleteMeal = useCallback(async (meal: V2Meal) => {
    if (deletingMealId || updatingMealPortionId) return;

    const beforeSnapshot = currentMealNotificationSnapshot;
    setDeletingMealId(meal.id);
    try {
      await ensureMiniAppAuth();
      await api.deleteMiniAppMeal(meal.id);

      setMealPendingDelete((current) => (current?.id === meal.id ? null : current));
      setMealActionTarget((current) => (current?.id === meal.id ? null : current));
      setMealPendingPortion((current) => (current?.id === meal.id ? null : current));
      if (selectedMeal?.id === meal.id) {
        closeSelectedMeal(meal.id);
      } else {
        setSelectedMeal((current) => (current?.id === meal.id ? null : current));
      }
      setDayMeals((current) => {
        const nextMeals = current.filter((item) => item.id !== meal.id);
        const stillHasMealsForDay = nextMeals.length > 0;
        const dateKey = formatLocalDate(selectedDate);
        setWeekMealDays((prev) => {
          const next = new Set(prev);
          if (stillHasMealsForDay) next.add(dateKey);
          else next.delete(dateKey);
          return next;
        });
        return nextMeals;
      });

      weekMarkersCacheRef.current.delete(formatLocalDate(weekStartDate));
      clearV2SessionCacheByPrefix(`home:${formatLocalDate(selectedDate)}`);
      clearV2SessionCacheByPrefix(`statistics:${formatLocalDate(selectedDate)}`);
      const loaded = await loadData(selectedDate, { silent: true, preferCache: false });
      if (loaded && isSameDay(selectedDate, today)) {
        const afterActivitySummary = loaded.activitiesData ? {
          totalCalories: roundNumber(loaded.activitiesData?.summary?.totalCalories),
          totalDuration: roundNumber(loaded.activitiesData?.summary?.totalDuration),
          count: roundNumber(loaded.activitiesData?.summary?.count),
        } : null;
        const afterSnapshot = buildMealNotificationSnapshot({
          meals: loaded.mealsData.meals,
          stats: loaded.statsData,
          profileData: loaded.profileData,
          activitySummary: afterActivitySummary,
          caloriesMode: (loaded.activitiesData?.caloriesMode as 'add_to_norm' | 'show_separate') || 'show_separate',
        });
        enqueueMealChangeNotifications(beforeSnapshot, afterSnapshot);
      }

      if (activeTab === 'statistics') {
        await loadStatisticsView(selectedDate, { preferCache: false });
      }
    } catch (deleteError: any) {
      console.error('Failed to delete meal in v2', deleteError);
      setError(deleteError?.message || 'Не получилось удалить прием пищи');
    } finally {
      setDeletingMealId(null);
    }
  }, [activeTab, api, buildMealNotificationSnapshot, closeSelectedMeal, currentMealNotificationSnapshot, deletingMealId, enqueueMealChangeNotifications, ensureMiniAppAuth, loadData, loadStatisticsView, selectedDate, selectedMeal?.id, today, updatingMealPortionId, weekStartDate]);

  const handleUpdateMealPortion = useCallback(async (meal: V2Meal, consumedPercent: number) => {
    if (deletingMealId || updatingMealPortionId) return;

    const beforeSnapshot = currentMealNotificationSnapshot;
    setUpdatingMealPortionId(meal.id);
    try {
      await ensureMiniAppAuth();
      const updatedMeal = await api.updateMiniAppMealPortion(meal.id, consumedPercent) as V2Meal;

      replaceMealInState(updatedMeal);
      setMealPendingPortion((current) => (current?.id === meal.id ? null : current));
      setMealActionTarget((current) => (current?.id === meal.id ? null : current));

      weekMarkersCacheRef.current.delete(formatLocalDate(weekStartDate));
      clearV2SessionCacheByPrefix(`home:${formatLocalDate(selectedDate)}`);
      clearV2SessionCacheByPrefix(`statistics:${formatLocalDate(selectedDate)}`);
      const loaded = await loadData(selectedDate, { silent: true, preferCache: false });
      if (loaded && isSameDay(selectedDate, today)) {
        const afterActivitySummary = loaded.activitiesData ? {
          totalCalories: roundNumber(loaded.activitiesData?.summary?.totalCalories),
          totalDuration: roundNumber(loaded.activitiesData?.summary?.totalDuration),
          count: roundNumber(loaded.activitiesData?.summary?.count),
        } : null;
        const afterSnapshot = buildMealNotificationSnapshot({
          meals: loaded.mealsData.meals,
          stats: loaded.statsData,
          profileData: loaded.profileData,
          activitySummary: afterActivitySummary,
          caloriesMode: (loaded.activitiesData?.caloriesMode as 'add_to_norm' | 'show_separate') || 'show_separate',
        });
        enqueueMealChangeNotifications(beforeSnapshot, afterSnapshot);
      }

      if (activeTab === 'statistics') {
        await loadStatisticsView(selectedDate, { preferCache: false });
      }
    } catch (updateError: any) {
      console.error('Failed to update meal portion in v2', updateError);
      setError(updateError?.message || 'Не получилось пересчитать приём пищи');
    } finally {
      setUpdatingMealPortionId(null);
    }
  }, [activeTab, api, buildMealNotificationSnapshot, currentMealNotificationSnapshot, deletingMealId, enqueueMealChangeNotifications, ensureMiniAppAuth, loadData, loadStatisticsView, replaceMealInState, selectedDate, today, updatingMealPortionId, weekStartDate]);

  const handleUpdateMealWeight = useCallback(async (
    meal: V2Meal,
    target: { kind: 'meal' } | { kind: 'ingredient'; index: number },
    weightGrams: number,
  ) => {
    if (deletingMealId || updatingMealPortionId || updatingMealWeightId) return;
    setUpdatingMealWeightId(meal.id);
    try {
      await ensureMiniAppAuth();
      const updatedMeal = await api.updateMiniAppMealWeight(meal.id, target, weightGrams) as V2Meal;
      replaceMealInState(updatedMeal);
      weekMarkersCacheRef.current.delete(formatLocalDate(weekStartDate));
      clearV2SessionCacheByPrefix(`home:${formatLocalDate(selectedDate)}`);
      clearV2SessionCacheByPrefix(`statistics:${formatLocalDate(selectedDate)}`);
      await loadData(selectedDate, { silent: true, preferCache: false });
      if (activeTab === 'statistics') await loadStatisticsView(selectedDate, { preferCache: false });
    } catch (updateError: any) {
      console.error('Failed to update meal weight in v2', updateError);
      setError(updateError?.message || 'Не получилось изменить вес');
      throw updateError;
    } finally {
      setUpdatingMealWeightId(null);
    }
  }, [activeTab, api, deletingMealId, ensureMiniAppAuth, loadData, loadStatisticsView, replaceMealInState, selectedDate, updatingMealPortionId, updatingMealWeightId, weekStartDate]);

  if (onboardingRequired) {
    const tgUser = (window as any).Telegram?.WebApp?.initDataUnsafe?.user;
    const tgStartParam = (window as any).Telegram?.WebApp?.initDataUnsafe?.start_param;
    const onboardingReferral = typeof tgStartParam === 'string' && /^[a-zA-Z0-9_-]{1,64}$/.test(tgStartParam) ? tgStartParam : null;
    return (
      <OnboardingScreen
        themeMode={themeMode}
        firstName={tgUser?.first_name || profile?.user?.firstName || null}
        referralCode={onboardingReferral}
        onCompleted={() => {
          try { api.setMiniAppOnboardingRequired(false); } catch { /* noop */ }
          setOnboardingRequired(false);
          try {
            localStorage.setItem('noma_v2_onboarding_tour_version', '3');
            localStorage.setItem('noma_v2_onboarding_coach_pending', '3');
          } catch { /* noop */ }
          setOnboardingCoachStep(0);
          window.setTimeout(() => setAddMealChoiceOpen(true), 320);
          void loadData(selectedDate, { preferCache: false });
        }}
      />
    );
  }

  const renderContent = () => {
    if (selectedMeal) {
      return (
        <MealDetailScreen
          meal={selectedMeal}
          onBack={() => closeSelectedMeal(selectedMeal.id)}
          onDelete={requestDeleteMeal}
          onUpdateWeight={handleUpdateMealWeight}
          isDeleting={deletingMealId === selectedMeal.id}
          isUpdatingWeight={updatingMealWeightId === selectedMeal.id}
          themeMode={themeMode}
        />
      );
    }

    if (standaloneScreen?.type === 'profile') {
      return (
        <ProfileScreen
          profile={profile}
          socialOverview={socialOverview}
          socialLoading={socialViewLoading}
          achievementsOverview={achievementsOverview}
          achievementsLoading={achievementsLoading}
          achievementsError={achievementsError}
          onBack={() => setStandaloneScreen(null)}
          onProfileRefresh={async () => { await loadData(selectedDate, { silent: true, preferCache: false }); }}
          onLoadSocial={loadSocialView}
          onLoadAchievements={loadAchievementsView}
          onRequestFriend={handleRequestFriend}
          onAcceptFriendRequest={handleAcceptFriendRequest}
          onDeclineFriendRequest={handleDeclineFriendRequest}
          onRemoveFriend={handleRemoveFriend}
          onUpdateSocialPrivacy={handleUpdateSocialPrivacy}
          onEnqueueNotification={enqueueNotification}
          themeMode={themeMode}
          onThemeModeChange={setThemeMode}
          onRestartOnboarding={restartOnboardingCoach}
          initialView={standaloneScreen.initialView}
          initialFocus={standaloneScreen.initialFocus || null}
          standalone={standaloneScreen.standalone}
          rootView={botLaunchRootScreen?.type === 'profile' ? botLaunchRootScreen.view : 'main'}
          hideRootBack={botLaunchRootScreen?.type === 'profile'}
        />
      );
    }

    if (standaloneScreen?.type === 'streak') {
      return (
        <StreakScreen
          profile={profile}
          achievementsOverview={achievementsOverview}
          achievementsLoading={achievementsLoading}
          themeMode={themeMode}
          onBack={() => setStandaloneScreen(null)}
        />
      );
    }

    if (standaloneScreen?.type === 'meal-planner') {
      return (
        <MealPlannerScreen
          anchorDate={selectedDate}
          onBack={() => setStandaloneScreen(null)}
          themeMode={themeMode}
          onOpenSubscription={(message?: string) => setStandaloneScreen({ type: 'profile', initialView: 'subscription', standalone: true })}
          hideRootBack={botLaunchRootScreen?.type === 'meal-planner'}
        />
      );
    }

    if (standaloneScreen?.type === 'add-meal-text') {
      return (
        <AddMealTextScreen
          onBack={() => setStandaloneScreen(null)}
          themeMode={themeMode}
          onSaved={() => { void loadData(selectedDate, { silent: true, preferCache: false }); }}
        />
      );
    }

    if (standaloneScreen?.type === 'add-meal-photo') {
      return (
        <AddMealPhotoScreen
          onBack={() => setStandaloneScreen(null)}
          themeMode={themeMode}
          onSaved={() => { void loadData(selectedDate, { silent: true, preferCache: false }); }}
        />
      );
    }

    if (activeTab === 'statistics') {
      return (
        <StatisticsScreen
          selectedDate={selectedDate}
          weekStats={weekStatsView}
          weekActivitySeries={weekActivitySeries}
          dayMeals={dayMeals}
          profile={profile}
          isLoading={statsViewLoading}
          themeMode={themeMode}
          onAddWeight={() => setStandaloneScreen({ type: 'profile', initialView: 'main', standalone: false, initialFocus: 'weight' })}
        />
      );
    }

    if (activeTab === 'activity') {
      return (
        <ActivityScreen
          selectedDate={selectedDate}
          activityDay={activityDay}
          weekActivitySeries={weekActivitySeries}
          isLoading={activityViewLoading}
          themeMode={themeMode}
          caloriesMode={activityCaloriesMode}
          activityGoal={activityGoal}
          onActivityChanged={handleActivityCreated}
          onCaloriesModeChange={handleActivityCaloriesModeChange}
          onSelectDate={(date) => {
            if (date.getTime() <= today.getTime()) {
              setSelectedDate(date);
            }
          }}
        />
      );
    }

    if (activeTab === 'social') {
      return (
        <CommunityScreen
          socialOverview={socialOverview}
          isLoading={socialViewLoading}
          themeMode={themeMode}
          onRequestFriend={handleRequestFriend}
          onAcceptFriendRequest={handleAcceptFriendRequest}
          onDeclineFriendRequest={handleDeclineFriendRequest}
          onRemoveFriend={handleRemoveFriend}
          onCreateFriendChallenge={handleCreateFriendChallenge}
          onAcceptFriendChallenge={handleAcceptFriendChallenge}
          onDeclineFriendChallenge={handleDeclineFriendChallenge}
          onSendFriendChallengeNudge={handleSendFriendChallengeNudge}
          onCancelFriendChallenge={handleCancelFriendChallenge}
          initialSection={socialRouteTarget?.section || null}
          initialChallengeId={socialRouteTarget?.challengeId || null}
          onConsumeInitialChallenge={consumeSocialChallengeRouteTarget}
        />
      );
    }

    return (
      <div className="noma-v2-screen pb-28">
        <section className="px-4 pt-3">
          <div className="mt-4 flex items-start justify-between gap-3">
            <div>
              <div className="text-[42px] font-black capitalize leading-[0.88] tracking-[-0.082em] text-[#111113]">
                {formatMonthTitle(selectedDate)}
              </div>
              <div className="mt-1 text-[18px] font-semibold tracking-[-0.03em] text-[#202020]">
                {selectedDate.getFullYear()}
              </div>
            </div>

            <div className="relative flex items-center gap-2.5 pt-2">
              <button type="button" className="noma-v2-pill noma-v2-pill--button" onClick={() => setStandaloneScreen({ type: 'streak' })}>
                <Flame className="h-4 w-4 text-[#7f54ff]" />
                <span>{Math.max(streakDays, 0)} {pluralize(Math.max(streakDays, 0), ['день', 'дня', 'дней'])}</span>
              </button>
              <button type="button" aria-label="Профиль и настройки" className="noma-v2-circle-button overflow-hidden" onClick={() => { setShowProfileHint(false); setStandaloneScreen({ type: 'profile', initialView: 'main', standalone: false }); }}>
                {(window as any).Telegram?.WebApp?.initDataUnsafe?.user?.photo_url ? (
                  <img src={(window as any).Telegram.WebApp.initDataUnsafe.user.photo_url} alt="" className="h-full w-full object-cover" />
                ) : <User className="h-5 w-5" />}
              </button>
              {showProfileHint ? (
                <button type="button" onClick={() => setShowProfileHint(false)} className={`absolute right-0 top-[58px] z-20 w-max max-w-[210px] rounded-[16px] px-3 py-2 text-left text-[12px] font-bold leading-4 shadow-[0_14px_30px_rgba(17,17,19,0.16)] ${themeMode === 'dark' ? 'bg-[#26222f] text-white' : 'bg-white text-[#111113]'}`}>
                  Здесь профиль, вес и настройки
                </button>
              ) : null}
            </div>
          </div>

          <div className="noma-v2-week-strip-shell mt-5">
            <button
              type="button"
              className="noma-v2-week-nav noma-v2-week-nav--left"
              onClick={() => shiftWeek(-1)}
              aria-label="Предыдущая неделя"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div
              className={`noma-v2-week-strip ${weekTransitionDirection ? `is-sliding-${weekTransitionDirection}` : ''}`}
              {...weekSwipeHandlers}
              style={{ touchAction: 'pan-y' }}
            >
              {currentWeekDates.map((date) => {
                const selected = isSameDay(date, selectedDate);
                const hasMeal = weekMealDays.has(formatLocalDate(date));
                const isFuture = date.getTime() > today.getTime();
                return (
                  <button
                    key={date.toISOString()}
                    type="button"
                    onClick={() => selectDay(date)}
                    disabled={isFuture}
                    className={`noma-v2-day ${selected ? 'noma-v2-day--selected' : ''} ${isFuture ? 'noma-v2-day--disabled' : ''}`}
                  >
                    <div className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#202020]">{formatWeekday(date)}</div>
                    <div className={`mt-2.5 text-[15px] font-extrabold tracking-[-0.04em] ${isFuture ? 'text-[#202020]' : 'text-[#111113]'}`}>{date.getDate()}</div>
                    <div className="noma-v2-day__dot-row">
                      {hasMeal ? <div className={`h-1.5 w-1.5 rounded-full ${selected ? 'bg-[#7f54ff]' : themeMode === 'dark' ? 'bg-[rgba(255,255,255,0.5)]' : 'bg-[rgba(32,32,32,0.28)]'}`} /> : null}
                    </div>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              className="noma-v2-week-nav noma-v2-week-nav--right"
              onClick={() => shiftWeek(1)}
              disabled={!canGoToNextWeek}
              aria-label="Следующая неделя"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {!isTodaySelected ? (
            <div className="noma-v2-week-strip-actions">
              <button type="button" className="noma-v2-today-chip" onClick={handleReturnToToday}>
                Сегодня
              </button>
            </div>
          ) : null}
        </section>

        <section className="px-4 pt-3.5">
          {error ? (
            <article className="mb-3 rounded-[24px] border border-[rgba(255,96,96,0.18)] bg-white/72 px-4 py-3 backdrop-blur-[6px]">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[15px] font-extrabold tracking-[-0.04em] text-[#111113]">Данные подгружаются с ошибкой</div>
                  <p className="mt-1 text-[13px] font-semibold leading-5 text-[#696975]">{error}</p>
                </div>
                <button type="button" onClick={() => loadData(selectedDate)} className="text-[13px] font-bold text-[#7f54ff]">
                  Обновить
                </button>
              </div>
            </article>
          ) : null}

          <>
            <article className="noma-v2-card noma-v2-calories-card relative p-4">
                <button
                  type="button"
                  onClick={handleOpenTips}
                  disabled={!isTodaySelected}
                  className="noma-v2-advice-corner"
                  aria-label={tipsPrompt}
                >
                  <Lightbulb className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="noma-v2-icon-shell">
                    <Flame className="h-5 w-5 text-[#111113]" />
                  </div>
                  <div>
                    <div className="text-[16px] font-extrabold tracking-[-0.05em] text-[#111113]">Калории</div>
                    <div className="text-[13px] font-semibold text-[#767682]">{selectedDateTitle}</div>
                  </div>
                  {isLoading ? <Loader2 className="ml-auto mr-14 h-4 w-4 animate-spin text-[#7f54ff]" /> : null}
                </div>

                <div className="mt-4 flex items-center gap-5">
                  <div className="text-[#7f54ff] noma-v2-calories-ring">
                    <ProgressRing
                      progress={caloriesProgress}
                      size={136}
                      strokeWidth={11}
                      trackColor={themeMode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(17,17,19,0.08)'}
                      center={
                        <div className="text-center">
                          <div className="text-[24px] font-black tracking-[-0.05em] text-[#111113]"><AnimatedNumber value={caloriesRemaining} /></div>
                          <div className="mt-1 text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#7e7e8a]">осталось</div>
                        </div>
                      }
                    />
                  </div>

                  <div className="min-w-[108px] text-left">
                    <div className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#9b9ba6]">съедено</div>
                    <div className="mt-1 text-[42px] font-black leading-none tracking-[-0.08em] text-[#111113]"><AnimatedNumber value={caloriesConsumed} /></div>
                    <div className="mt-2 flex items-center gap-2 text-[12px] font-extrabold uppercase leading-4 tracking-[0.12em] text-[#8a8a95]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[rgba(17,17,19,0.2)]" />
                      из <AnimatedNumber value={caloriesTarget} /> ккал
                    </div>
                  </div>
                </div>

                <WaterTrackerButton
                  totalMl={waterTotalMl}
                  targetMl={waterTargetMl}
                  isSaving={waterSaving}
                  disabled={!isTodaySelected}
                  splashKey={waterSplashKey}
                  onAdd={handleAddWater}
                />
              </article>

            <div className="mt-4 grid grid-cols-3 gap-3">
              {(['protein', 'fats', 'carbs'] as MacroKey[]).map((key) => {
                const theme = getMacroTheme(key);
                return (
                  <MacroCard
                    key={key}
                    label={theme.label}
                    current={macroValues[key].current}
                    target={macroValues[key].target}
                    theme={theme}
                    trackColor={themeMode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(17,17,19,0.08)'}
                  />
                );
              })}
            </div>

            {isTodaySelected && coursePlan ? (
              <button type="button" onClick={() => switchTab('statistics')} className="noma-v2-card mt-4 flex min-h-[62px] w-full items-center gap-3 px-3.5 py-2.5 text-left">
                <div className={`grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-[15px] ${themeMode === 'dark' ? 'bg-[rgba(127,84,255,0.16)]' : 'bg-[rgba(127,84,255,0.1)]'}`}>
                  <img src={courseMascotSrc} alt="" className="h-11 w-11 object-contain object-bottom" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[9px] font-black uppercase tracking-[0.15em] text-[#8a8a96]">Курс к цели</div>
                  <div className={`mt-0.5 truncate text-[14px] font-black tracking-[-0.025em] ${themeMode === 'dark' ? 'text-white' : 'text-[#111113]'}`}>
                    {courseTitle}{courseActualRate != null ? ` · ${courseActualRate.toFixed(1)} кг/нед.` : ''}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-[#8b63ff]" />
              </button>
            ) : null}

            <section className="mt-7">
              <div className="flex items-center gap-3">
                <h2 className="text-[32px] font-black tracking-[-0.065em] text-[#111113]">Приемы пищи</h2>
                {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin text-[#7f54ff]" /> : null}
              </div>

              <div className="mt-4 space-y-4">
                {mealSections.map((section) => (
                  <article key={section.category} className="noma-v2-card p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="text-[22px] font-extrabold tracking-[-0.05em] text-[#111113]">{section.label}</div>
                      <div className={`text-[16px] font-extrabold tracking-[-0.04em] ${section.meals.length > 0 ? 'text-[#111113]' : 'text-[#b3b0bf]'}`}>{section.totalCalories} ккал</div>
                    </div>

                    <div className={`mt-3 ${section.meals.length > 0 ? 'space-y-2.5' : ''}`}>
                      {section.meals.length > 0 ? section.meals.map((meal) => (
                        <SwipeableMealRow
                          key={meal.id}
                          meal={meal}
                          onOpen={setSelectedMeal}
                          onLongPress={openMealActions}
                          onDelete={requestDeleteMeal}
                          isDeleting={deletingMealId === meal.id}
                          themeMode={themeMode}
                        />
                      )) : isLoading ? (
                        <SkeletonMealRow />
                      ) : (
                        <button type="button" onClick={() => setAddMealChoiceOpen(true)} className={`flex w-full items-center justify-between rounded-[18px] px-4 py-3 text-left ${themeMode === 'dark' ? 'bg-[rgba(255,255,255,0.04)]' : 'bg-[#f7f7fa]'}`}>
                          <span className="text-[14px] font-semibold tracking-[-0.02em] text-[#9a98a6]">Добавить {section.label.toLowerCase()}</span>
                          <Plus className="h-4 w-4 text-[#7f54ff]" />
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </>
        </section>
      </div>
    );
  };

  return (
    <div className={`noma-v2 ${themeMode === 'dark' ? 'is-dark' : ''}`}>
      <AdviceModalOverlay
        advice={tipsAdvice}
        themeMode={themeMode}
        isOpen={tipsModalOpen}
        isLoading={tipsModalLoading}
        onClose={() => {
          if (!tipsModalLoading) setTipsModalOpen(false);
        }}
      />
      <SocialReactionPromptOverlay
        prompt={activeSocialPrompt}
        themeMode={themeMode}
        isSubmitting={socialPromptActionId === activeSocialPrompt?.id}
        onCongratulate={() => {
          void handleCongratulateSocialPrompt();
        }}
        onDismiss={() => {
          void handleDismissSocialPrompt();
        }}
      />
      <V2NotificationOverlay
        notification={activeNotification}
        isLeaving={notificationLeaving}
        onDismiss={() => dismissActiveNotification(true)}
      />

      {renderContent()}

      <MealActionsSheet
        meal={mealActionTarget}
        themeMode={themeMode}
        onClose={() => {
          if (!deletingMealId && !updatingMealPortionId) setMealActionTarget(null);
        }}
        onAdjustPortion={openMealPortionSheet}
        onDelete={requestDeleteMeal}
      />

      {addMealChoiceOpen ? (
        <AddMealChoiceSheet
          themeMode={themeMode}
          onClose={() => setAddMealChoiceOpen(false)}
          onChoosePhoto={() => { setAddMealChoiceOpen(false); setStandaloneScreen({ type: 'add-meal-photo' }); }}
          onChooseText={() => { setAddMealChoiceOpen(false); setStandaloneScreen({ type: 'add-meal-text' }); }}
          onRepeatMeal={async (item) => {
            await ensureMiniAppAuth();
            await api.createMiniAppMeal({
              category: item.category,
              name: item.name,
              calories: item.calories,
              protein: item.protein,
              carbs: item.carbs,
              fats: item.fats,
              weight: item.weight > 0 ? item.weight : undefined,
            });
            clearV2SessionCacheByPrefix(`home:${formatLocalDate(selectedDate)}`);
            setAddMealChoiceOpen(false);
            await loadData(selectedDate, { silent: true });
          }}
        />
      ) : null}

      <MealPortionSheet
        meal={mealPendingPortion}
        consumedPercent={mealConsumedDraft}
        isSubmitting={updatingMealPortionId === mealPendingPortion?.id}
        themeMode={themeMode}
        onCancel={() => {
          if (!updatingMealPortionId) setMealPendingPortion(null);
        }}
        onChange={setMealConsumedDraft}
        onConfirm={handleUpdateMealPortion}
      />

      <DeleteMealConfirmSheet
        meal={mealPendingDelete}
        isDeleting={deletingMealId === mealPendingDelete?.id}
        themeMode={themeMode}
        onCancel={() => {
          if (!deletingMealId) setMealPendingDelete(null);
        }}
        onConfirm={handleDeleteMeal}
      />

      {activeCoachStep && !selectedMeal && !standaloneScreen && !addMealChoiceOpen ? (
        <div className="noma-v2-onb-coach noma-v2-onb-coach--tour" role="dialog" aria-modal="true" aria-label="Знакомство с Noma">
          <div className="noma-v2-onb-coach__shade" />
          <div className="noma-v2-onb-coach__card">
            <div className="noma-v2-onb-coach__progress" aria-label={`Шаг ${activeCoachIndex + 1} из ${coachSteps.length}`}>{coachSteps.map((_, index) => <i key={index} className={index <= activeCoachIndex ? 'is-active' : ''} />)}</div>
            <div className="noma-v2-onb-coach__preview"><div className="noma-v2-onb-coach__preview-glow" /><div className="noma-v2-onb-coach__icon">{activeCoachStep.icon}</div><span>{activeCoachStep.eyebrow}</span></div>
            <div className="noma-v2-onb-coach__copy">
              <span>Шаг {activeCoachIndex + 1} из {coachSteps.length}</span>
              <strong>{activeCoachStep.title}</strong>
              <p>{activeCoachStep.text}</p>
            </div>
            <div className="noma-v2-onb-coach__actions">
              <button type="button" className="is-skip" onClick={finishOnboardingCoach}>Пропустить</button>
              <button type="button" className="is-next" onClick={() => {
                if (activeCoachIndex >= coachSteps.length - 1) {
                  finishOnboardingCoach();
                } else {
                  setOnboardingCoachStep((step) => step === null ? null : step + 1);
                }
              }}>{activeCoachIndex >= coachSteps.length - 1 ? 'Начать' : 'Дальше'}<ChevronRight /></button>
            </div>
          </div>
        </div>
      ) : null}

      {!selectedMeal && !standaloneScreen ? (
        <nav className={`noma-v2-bottom-nav noma-v2-bottom-nav--with-fab ${activeCoachStep ? 'is-coach-active' : ''}`}>
          {tabs.slice(0, 2).map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => switchTab(tab.id)}
                className={`noma-v2-bottom-nav__item ${active ? 'is-active' : ''} ${activeCoachStep?.tab === tab.id && onboardingCoachStep !== 1 ? 'is-coach-highlight' : ''}`}
                aria-label={tab.label}
              >
                <span className="noma-v2-bottom-nav__icon">{tab.icon}</span>
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => setAddMealChoiceOpen(true)}
            className={`noma-v2-bottom-nav__fab ${onboardingCoachStep === 1 && !addMealChoiceOpen ? 'is-coach-highlight' : ''}`}
            aria-label="Добавить приём пищи"
          >
            <Plus className="h-6 w-6" />
          </button>

          {tabs.slice(2).map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => switchTab(tab.id)}
                className={`noma-v2-bottom-nav__item ${active ? 'is-active' : ''} ${activeCoachStep?.tab === tab.id ? 'is-coach-highlight' : ''}`}
                aria-label={tab.label}
              >
                <span className="noma-v2-bottom-nav__icon">{tab.icon}</span>
              </button>
            );
          })}
        </nav>
      ) : null}
    </div>
  );
}
